import { Edge, Node } from '@xyflow/react';

import {
	DomainMetadataUpdateItem,
	DomainNodeData,
	layoutNodes,
	toDomainMetadataUpdateItem,
	withPositionMetadata,
} from './DomainGraphUtils';

export function getGraphStateKey(nodes: Node<DomainNodeData>[], edges: Edge[]) {
	const nodeKey = nodes
		.map((node) => `${node.id}:${node.position.x}:${node.position.y}:${node.selected ? '1' : '0'}:${node.data.kind}`)
		.join('|');
	const edgeKey = edges.map((edge) => `${edge.id}:${edge.source}:${edge.target}:${edge.sourceHandle ?? ''}:${edge.targetHandle ?? ''}`).join('|');

	return `${nodeKey}::${edgeKey}`;
}

export function applyDomainGraphLayout({
	currentNodes,
	visibleNodes,
	visibleEdges,
}: {
	currentNodes: Node<DomainNodeData>[];
	visibleNodes: Node<DomainNodeData>[];
	visibleEdges: Edge[];
}): {
	nodes: Node<DomainNodeData>[];
	edges: Edge[];
	metadataItems: DomainMetadataUpdateItem[];
} {
	const currentNodeById = new Map(currentNodes.map((node) => [node.id, node]));
	const currentSelectedById = new Map(currentNodes.map((node) => [node.id, node.selected]));
	const visibleNodeIds = new Set(visibleNodes.map((node) => node.id));
	const nodesToLayout = visibleNodes.map((node) => currentNodeById.get(node.id) ?? node);
	const edgesToLayout = visibleEdges.filter((edge) => visibleNodeIds.has(edge.source) && visibleNodeIds.has(edge.target));

	if (nodesToLayout.length === 0) {
		return {
			nodes: [],
			edges: edgesToLayout,
			metadataItems: [],
		};
	}

	const layoutedNodes = layoutNodes(nodesToLayout, edgesToLayout, false).map(withPositionMetadata);
	const layoutedNodeById = new Map(layoutedNodes.map((node) => [node.id, node]));
	const nodes = visibleNodes.map((node) => {
		const layoutedNode = layoutedNodeById.get(node.id);
		return layoutedNode ? { ...layoutedNode, selected: currentSelectedById.get(node.id) ?? layoutedNode.selected } : node;
	});

	return {
		nodes,
		edges: edgesToLayout,
		metadataItems: layoutedNodes.map(toDomainMetadataUpdateItem),
	};
}
